import { basePostHogClientOptions } from '@bazak/analytics/client';
import posthog from 'posthog-js';

import { DEPLOY_ENV, IS_TRACKING_ENABLED, POSTHOG_HOST } from '@/common/constants';
import { isInIframe } from '@/lib/utils/dom';
import {
  type BootstrapCookieValue,
  readBootstrapCookie,
} from '@/lib/utils/posthog-bootstrap-client';
import { clearUrlParam, getUrlParam } from '@/lib/utils/url-params';

interface PostHogBootstrap extends BootstrapCookieValue {
  isIdentifiedID?: boolean;
}

// The T4 verification page is the one route where a guest types a government
// ID and a one-time code. Analytics never loads there, so no autocapture, session
// recording or pageview can carry those values to a third party.
const ANALYTICS_FREE_PATHS = ['/t4/verify'];
const isAnalyticsFreePath = ANALYTICS_FREE_PATHS.includes(
  window.location.pathname.replace(/\/$/, ''),
);

if (IS_TRACKING_ENABLED && process.env.NEXT_PUBLIC_POSTHOG_KEY && !isAnalyticsFreePath) {
  let bootstrap: PostHogBootstrap | undefined = readBootstrapCookie() ?? undefined;

  const inIframe = isInIframe();

  // Parent widget injects its first-party distinct_id via `?parentDistinctId=…`
  // (see `widget-core.ts`). When present it must beat the bootstrap cookie —
  // the cookie is third-party on bazak.ai and unreliable on mobile, while the
  // URL param traces back to the parent's first-party localStorage.
  //
  // `isIdentifiedID: true` is critical: it makes posthog merge any pre-existing
  // anonymous identity (e.g. a prior session whose localStorage survived ITP)
  // into the parent id via $identify, instead of silently overwriting it.
  // See posthog-core.ts `_init` — without this flag, the ELSE branch fires and
  // the prior anonymous session is orphaned.
  //
  // We also drop the cookie-supplied sessionID: it was paired with the old
  // distinct_id; pairing it with a different one risks stitching one user's
  // session under another's identity on shared devices.
  if (inIframe) {
    const parentDistinctId = getUrlParam('parentDistinctId');
    if (parentDistinctId) {
      bootstrap = { distinctID: parentDistinctId, isIdentifiedID: true };
      // Keep parentDistinctId out of Referer headers, document.referrer
      // snapshots, and error-report URLs by stripping it from history.
      clearUrlParam('parentDistinctId');
    }
  }

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    ...basePostHogClientOptions({ host: POSTHOG_HOST }),
    // The shared defaults switch flags off — they cost a request per load and most apps never read
    // one. This app does: the rooms experiment is assigned in the browser, on the id that carries the
    // guest's order. With flags off, `getFeatureFlag` answers `undefined` forever, which reads as
    // "no experiment" and quietly hands every guest the control experience.
    advanced_disable_feature_flags: false,
    advanced_disable_feature_flags_on_first_load: false,
    // In-iframe (widget): the host page owns recording/pageviews/autocapture.
    disable_session_recording: inIframe,
    capture_pageview: !inIframe,
    capture_pageleave: !inIframe,
    autocapture: !inIframe,
    before_send: event => {
      if (event?.event === '$exception') {
        const exceptions = event.properties?.$exception_list;
        if (
          Array.isArray(exceptions) &&
          exceptions.some((e: { value?: string }) => e.value && /ResizeObserver loop/.test(e.value))
        ) {
          return null;
        }
      }
      return event;
    },
    ...(bootstrap && { bootstrap }),
  });

  // Stamp `environment` as a super-property so EVERY event (autocapture,
  // pageview, manual `posthog.capture`) carries it from the first event.
  // `WidgetAnalyticsProvider` also calls `register({ environment })` later
  // with tenantKey/widgetVersion, but that runs inside `useLayoutEffect` and
  // would miss the initial pageview without this earlier call.
  posthog.register({ environment: DEPLOY_ENV });
}
